import { useCallback, useEffect, useState } from "react";
import {
  CommandDashboardPageService,
  type CommandDashboardOperationsBundle,
  type CommandDashboardPeriod,
  type CommandDashboardStrategicBundle,
} from "@/services/command-dashboard-page.service";

const emptyStrategic = (period: CommandDashboardPeriod): CommandDashboardStrategicBundle => ({
  summary: {
    period,
    totalLeads: 0,
    wonCount: 0,
    bookedRevenue: 0,
    pendingRevenue: 0,
    netJobs: 0,
    netRevenue: 0,
    installsScheduled: 0,
    missingResults: 0,
    overdueFollowups: 0,
    reschedulesNeeded: 0,
    avgCpa: 0,
    clusteringPct: 0,
  },
  actions: [],
  channels: [],
  reps: [],
});

const emptyOperations = (): CommandDashboardOperationsBundle => ({
  summary: {
    activeJobs: 0,
    jobsToday: 0,
    blockedJobs: 0,
    atRisk48h: 0,
    notReadyJobs: 0,
    openIssues: 0,
    todayJobs: 0,
    tomorrowJobs: 0,
    unscheduledJobs: 0,
    inProgressJobs: 0,
    blockedRevenue: 0,
    atRiskRevenue: 0,
    unscheduledRevenue: 0,
    criticalJobs: 0,
    orderMaterialsCount: 0,
    submitPermitsCount: 0,
    assignCrewCount: 0,
    resolveIssuesCount: 0,
    intakeReviewRequiredCount: 0,
  },
  rows: [],
  count: 0,
});

export function useCommandDashboardStrategicData(query: { enabled?: boolean; period: CommandDashboardPeriod }) {
  const [refreshToken, setRefreshToken] = useState(0);
  const [data, setData] = useState<CommandDashboardStrategicBundle>(() => emptyStrategic(query.period));
  const [error, setError] = useState<unknown | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!query.enabled) {
      setData(emptyStrategic(query.period));
      setError(null);
      setLoading(false);
      return () => { cancelled = true; };
    }

    setLoading(true);
    void CommandDashboardPageService.getStrategicBundle({ period: query.period, refreshToken }).then((result) => {
      if (cancelled) return;
      setData(result.data);
      setError(result.error);
      setLoading(false);
    });

    return () => { cancelled = true; };
  }, [query.enabled, query.period, refreshToken]);

  const refresh = useCallback(() => setRefreshToken((value) => value + 1), []);
  return { ...data, error, loading, refresh };
}

export function useCommandDashboardOperationsData(query: { enabled?: boolean; page?: number; pageSize?: number }) {
  const [refreshToken, setRefreshToken] = useState(0);
  const [data, setData] = useState<CommandDashboardOperationsBundle>(() => emptyOperations());
  const [error, setError] = useState<unknown | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!query.enabled) {
      setData(emptyOperations());
      setError(null);
      setLoading(false);
      return () => { cancelled = true; };
    }

    setLoading(true);
    void CommandDashboardPageService.getOperationsBundle({
      page: query.page ?? 0,
      pageSize: query.pageSize ?? 10,
      refreshToken,
    }).then((result) => {
      if (cancelled) return;
      setData(result.data);
      setError(result.error);
      setLoading(false);
    });

    return () => { cancelled = true; };
  }, [query.enabled, query.page, query.pageSize, refreshToken]);

  const refresh = useCallback(() => setRefreshToken((value) => value + 1), []);
  return { ...data, error, loading, refresh };
}
